<template>
  <el-dialog
    :visible.sync="visible"
    :title="dialogTitle"
    width="600px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    append-to-body
    class="oj-result-modal"
  >
    <!-- Loading状态 -->
    <div v-if="status === 'loading'" class="loading-content">
      <div class="loading-spinner">
        <i class="el-icon-loading"></i>
      </div>
      <p class="loading-text">正在计算判题结果...</p>
    </div>

    <!-- 结果展示状态 -->
    <div v-else-if="status === 'result'" class="result-content">
      <div class="result-summary">
        <span class="summary-text">
          总计测试了<span class="highlight">{{ result.total }}</span
          >个数据， 其中通过<span class="highlight success">{{ result.passed }}</span
          >个， 不通过<span class="highlight error">{{ result.failed }}</span
          >个， 点击可下载<span class="download-link" @click="handleDownloadCode">源代码</span>
        </span>
      </div>

      <div class="test-cases">
        <div
          v-for="(testCase, index) in result.testCases"
          :key="index"
          class="test-case"
          :class="testCase.status"
        >
          <div class="case-header">
            <span class="case-label">测试数据{{ index + 1 }}</span>
            <div class="case-status-group">
              <span class="case-status">{{ testCase.status }}</span>
              <i :class="getStatusIcon(testCase.status)"></i>
            </div>
          </div>
          <div class="case-details">
            <div class="case-info">
              <span>运行时间: {{ testCase.runtime }}</span>
              <span>运行内存: {{ testCase.memory }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 超时状态 -->
    <div v-else-if="status === 'timeout'" class="timeout-content">
      <div class="timeout-icon">
        <i class="el-icon-warning"></i>
      </div>
      <p class="timeout-text">本次提交结果超时，可能服务器状况不佳，请重新提交或返回编辑代码。</p>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" :disabled="status === 'loading'" @click="handleConfirm">
        {{ confirmButtonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import TartIframeApi from '@/utils/tartIframeApi'
import { Dialog, Button } from 'element-ui'
export default {
  name: 'OJResultModal',
  components: {
    ElDialog: Dialog,
    ElButton: Button
  },
  data() {
    return {
      visible: false,
      status: 'loading', // 'loading', 'result', 'timeout'
      result: {
        total: 0,
        passed: 0,
        failed: 0,
        testCases: []
      },
      timeoutTimer: null,
      submissionId: null
    }
  },
  computed: {
    dialogTitle() {
      return 'OJ判题结果'
    },
    confirmButtonText() {
      if (this.status === 'loading') {
        return '判题中...'
      } else if (this.status === 'timeout') {
        return '重试'
      } else {
        return '确定'
      }
    }
  },
  beforeDestroy() {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer)
    }
    if (this.submissionId && this.status === 'loading') {
      TartIframeApi.cancelRun(this.submissionId)
    }
  },
  methods: {
    // 打开弹窗并开始判题
    open() {
      this.submissionId = null
      this.visible = true
      this.status = 'loading'
      this.result = {
        total: 0,
        passed: 0,
        failed: 0,
        testCases: []
      }

      // 设置超时定时器（30秒后自动超时）
      this.timeoutTimer = setTimeout(() => {
        if (this.status === 'loading') {
          this.status = 'timeout'
        }
      }, 30000)
    },
    setSubmissionId(submissionId) {
      this.submissionId = submissionId
    },

    // 显示判题结果
    showResult(resultData) {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer)
        this.timeoutTimer = null
      }

      this.status = 'result'
      this.result = {
        total: resultData.total || 0,
        passed: resultData.passed || 0,
        failed: resultData.failed || 0,
        testCases: resultData.testCases || []
      }
    },

    // 处理取消按钮点击
    handleCancel() {
      this.close()
    },

    // 处理确定按钮点击
    handleConfirm() {
      if (this.status === 'timeout') {
        this.submissionId && TartIframeApi.cancelRun(this.submissionId)
        // 重新提交
        this.$emit('retry')
      } else {
        // 确定关闭
        this.$emit('confirm', this.result)
      }
      this.close()
    },

    showTimeout() {
      this.status = 'timeout'
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer)
        this.timeoutTimer = null
      }
    },

    // 关闭弹窗
    close() {
      if (this.timeoutTimer) {
        clearTimeout(this.timeoutTimer)
        this.timeoutTimer = null
      }
      if (this.submissionId && this.status === 'loading') {
        TartIframeApi.cancelRun(this.submissionId)
      }
      this.$nextTick(() => {
        this.status = 'loading'
      })
      this.visible = false
    },
    getStatusIcon(status) {
      if (status !== 'AC') {
        return 'el-icon-close'
      } else {
        return 'el-icon-check'
      }
    },
    handleDownloadCode() {
      this.$emit('downloadCode')
    }
  }
}
</script>

<style lang="scss" scoped>
.oj-result-modal {
  ::v-deep .el-dialog__header {
    padding: 20px 20px 10px;
    border-bottom: 1px solid #f0f0f0;
  }

  ::v-deep .el-dialog__body {
    padding: 20px;
    min-height: 200px;
  }

  ::v-deep .el-dialog__footer {
    padding: 10px 20px 20px;
    border-top: 1px solid #f0f0f0;
  }
}

// Loading状态样式
.loading-content {
  text-align: center;
  padding: 40px 0;

  .loading-spinner {
    font-size: 32px;
    color: #409eff;
    margin-bottom: 20px;

    i {
      animation: rotating 2s linear infinite;
    }
  }

  .loading-text {
    font-size: 16px;
    color: #666;
    margin: 0;
  }
}

// 结果展示状态样式
.result-content {
  .result-summary {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 20px;

    .summary-text {
      font-size: 14px;
      color: #333;
      line-height: 1.5;
    }

    .highlight {
      font-weight: bold;

      &.success {
        color: #67c23a;
      }

      &.error {
        color: #f56c6c;
      }
    }

    .download-link {
      color: #409eff;
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .test-cases {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;

    .test-case {
      border: 1px solid #e4e7ed;
      border-radius: 6px;
      overflow: hidden;
      background-color: #fff;

      // 默认为失败状态 - 红色
      & {
        border-color: #f56c6c;

        .case-header {
          background-color: #fef0f0;
          border-bottom: 1px solid #f56c6c;
        }

        .case-status {
          color: #f56c6c;
          font-weight: bold;
        }

        i {
          color: #f56c6c;
        }
      }

      // 成功状态 - 绿色
      &.AC {
        border-color: #67c23a;

        .case-header {
          background-color: #f0f9ff;
          border-bottom: 1px solid #67c23a;
        }

        .case-status {
          color: #67c23a;
          font-weight: bold;
        }

        i {
          color: #67c23a;
        }
      }

      .case-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: #fafafa;

        .case-label {
          font-weight: 500;
          color: #333;
          font-size: 14px;
        }

        .case-status-group {
          display: flex;
          align-items: center;
          gap: 8px;

          .case-status {
            font-weight: bold;
            font-size: 14px;
          }

          i {
            font-size: 16px;
          }
        }
      }

      .case-details {
        padding: 12px 16px;
        background-color: #fff;

        .case-info {
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 14px;
          color: #666;

          span {
            display: flex;
            align-items: center;
          }
        }
      }
    }
  }
}

// 超时状态样式
.timeout-content {
  text-align: center;
  padding: 40px 20px;

  .timeout-icon {
    font-size: 48px;
    color: #e6a23c;
    margin-bottom: 20px;
  }

  .timeout-text {
    font-size: 16px;
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
}

// 按钮样式
.dialog-footer {
  text-align: right;

  .el-button {
    min-width: 80px;
  }
}

// 旋转动画
@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
